import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Button, Tooltip, Fab, Snackbar, Alert, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useDispatch, useSelector } from 'react-redux';
import KanbanBoard from '../../components/apps/kanban/KanbanBoard';
import CreateBoardModal from '../../components/apps/kanban/CreateBoardModal';
import CreateColumnModal from '../../components/apps/kanban/CreateColumnModal';
import KanbanDateFilter from '../../components/apps/kanban/KanbanDateFilter';
import { getColumnsPaginated, createColumn, updateColumnPositionsBatch } from '../../reducers/kanban/columnActions';
import { useToken } from '../../hooks/useToken';
import { GET_COLUMNS_ERROR } from '../../reducers/kanban/kanbanTypes';
import TicketDetailModal from '../../components/apps/kanban/TicketDetailModal';

/**
 * Componente para la vista Kanban de tableros generales.
 * Permite visualizar y gestionar columnas y tickets en un tablero Kanban.
 *
 * @param {string} boardId - ID del tablero a mostrar
 * @param {boolean} isDetailView - Indica si se muestra como vista detallada dentro de otro componente
 * @param {object} board - Datos del tablero (opcional, se usa para mostrar información adicional)
 */
const GeneralKanbanView = ({ boardId, isDetailView = false, board: initialBoardData = null }) => {
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();

  // Estado para los modales
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createColumnModalOpen, setCreateColumnModalOpen] = useState(false);

  // Estado para notificaciones
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Estados para el modal de detalles del ticket
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [ticketIdToOpenDetails, setTicketIdToOpenDetails] = useState(null);

  // Referencia para evitar bucles infinitos
  const prevBoardIdRef = React.useRef(null);

  // Referencia al KanbanBoard para recargar tickets
  const kanbanBoardRef = useRef(null);

  // Determinar si se debe mostrar el botón de crear tablero
  const showCreateButton = !isDetailView;

  // Determinar si se debe mostrar el botón de crear columna
  const showCreateColumnButton = true;

  /**
   * Muestra notificación al usuario con mensaje y severidad específicos
   * Actualiza el estado de notificación para mostrar un mensaje emergente.
   * Memoizado con useCallback para evitar recreaciones innecesarias.
   *
   * @param {string} message - Mensaje a mostrar en la notificación
   * @param {string} severity - Tipo de notificación (info, success, warning, error)
   */
  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  /**
   * Cierra la notificación actual manteniendo los demás parámetros del estado.
   * Utiliza el patrón de actualización funcional para garantizar el estado previo.
   * Memoizado con useCallback para evitar recreaciones innecesarias.
   */
  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  }, []);

  /**
   * Carga las columnas del tablero desde el endpoint /kanban/boards/{board_id}/columns.
   * Implementa verificaciones para evitar cargas duplicadas y maneja errores apropiadamente.
   * Utiliza el patrón de cleanup para evitar actualizaciones de estado en componentes desmontados.
   */
  useEffect(() => {
    if (!boardId) return;

    // Si es el mismo tablero y ya se intentó cargar, no volver a intentar
    if (prevBoardIdRef.current === boardId) {
      return;
    }

    // Guardar el ID del tablero actual
    prevBoardIdRef.current = boardId;

    let isMounted = true;

    const fetchColumns = async () => {
      try {
        // Obtener el token de autenticación
        const token = await getTokenSilent();

        if (!token) {
          throw new Error('No se pudo obtener el token de autenticación');
        }

        // Llamar a la acción para obtener las primeras 5 columnas (paginado)
        const result = await dispatch(getColumnsPaginated(boardId, 0, 5, token));

        if (isMounted && result.error) {
          showNotification(`Error: ${result.error}`, 'error');
        } else if (isMounted) {
          // Columnas cargadas exitosamente
        }
      } catch (error) {
        if (!isMounted) return;

        console.error('Error al cargar columnas:', error);

        dispatch({
          type: GET_COLUMNS_ERROR,
          payload: error.message || 'Error al cargar las columnas del tablero'
        });

        showNotification('Error al cargar las columnas: ' + (error.message || 'Error desconocido'), 'error');
      }
    };

    // Llamar inmediatamente a la función
    fetchColumns();

    return () => {
      isMounted = false;
    };
  }, [boardId, dispatch, getTokenSilent, showNotification]);

  // Obtener las columnas y filtros del estado de Redux usando useSelector
  // Adaptación a la nueva estructura del estado después de la refactorización
  const { columns: { items: columns = [] } = { items: [] }, tickets: ticketsState } = useSelector(state => state.kanban); // Renombrado tickets a ticketsState para evitar conflicto
  const { items: allTicketsForBoard = [] } = ticketsState || { items: [] }; // <-- AÑADIDO: para obtener la lista de tickets
  const dateFilters = useMemo(() => ticketsState?.filters || {}, [ticketsState?.filters]);

  // useEffect para manejar la apertura del modal de detalles del ticket <-- AÑADIDO
  useEffect(() => {
    if (ticketIdToOpenDetails) {
      const ticketToOpen = allTicketsForBoard.find(t => String(t.id) === String(ticketIdToOpenDetails));

      if (ticketToOpen) {
        setSelectedTicket(ticketToOpen);
        setDetailModalOpen(true);
      }
      setTicketIdToOpenDetails(null);
    }
  }, [ticketIdToOpenDetails, allTicketsForBoard]);

  /** Formatea las columnas para el componente KanbanBoard */
  const formattedColumns = useMemo(() => {

    // Filtrar primero por pertenencia al tablero y luego por tipo
    const boardColumns = columns.filter(column => {
      try {
        // Verificar si la columna pertenece a este tablero
        const belongsToBoard = String(column.board_id) === String(boardId);

        return belongsToBoard;
      } catch (error) {
        console.error('Error al procesar metadatos de columna:', error);
        return false;
      }
    });

    // Ordenar las columnas dinámicamente por su posición numérica
    const sortedColumns = [...boardColumns].sort((columnaActual, columnaSiguiente) => {
      // Si alguna columna no tiene posición válida, la ponemos al final
      const tienePosicionActual = columnaActual.position !== undefined && columnaActual.position !== null && columnaActual.position !== '' && !isNaN(Number(columnaActual.position));
      const tienePosicionSiguiente = columnaSiguiente.position !== undefined && columnaSiguiente.position !== null && columnaSiguiente.position !== '' && !isNaN(Number(columnaSiguiente.position));
      if (!tienePosicionActual && !tienePosicionSiguiente) return 0;
      if (!tienePosicionActual) return 1;
      if (!tienePosicionSiguiente) return -1;
      const posicionActual = Number(columnaActual.position);
      const posicionSiguiente = Number(columnaSiguiente.position);
      return posicionActual - posicionSiguiente;
    });

    // Formatear las columnas para el componente KanbanBoard
    return sortedColumns.map(column => ({
      id: column.id.toString(),
      name: column.name,
      position: column.position,
      tickets: allTicketsForBoard.filter(ticket => String(ticket.column_id) === String(column.id)) || [],
      meta_data: column.meta_data,
      board_id: column.board_id
    }));
  }, [boardId, columns, allTicketsForBoard]);

  // Referencia para rastrear los filtros anteriores y evitar bucles infinitos
  const prevFiltersRef = useRef(null);

  // Efecto para mostrar notificación cuando cambian los filtros de fecha
  useEffect(() => {
    let isMounted = true;

    // Comparar los filtros actuales con los anteriores para evitar recargas innecesarias
    const prevFilters = prevFiltersRef.current;
    const currentFiltersStr = JSON.stringify(dateFilters);
    const prevFiltersStr = prevFilters ? JSON.stringify(prevFilters) : null;

    // Si los filtros no han cambiado, no hacer nada
    if (prevFiltersStr === currentFiltersStr) {
      return;
    }

    // Actualizar la referencia de los filtros anteriores
    prevFiltersRef.current = dateFilters;

    // Mostrar notificación cuando se aplican o limpian filtros
    if (isMounted) {
      if (dateFilters.startDate || dateFilters.endDate) {
        // Cuando se aplican filtros
        const filterMessage = dateFilters.startDate && dateFilters.endDate
          ? `Mostrando tickets del ${dateFilters.startDate} al ${dateFilters.endDate}`
          : dateFilters.startDate
            ? `Mostrando tickets desde el ${dateFilters.startDate}`
            : `Mostrando tickets hasta el ${dateFilters.endDate}`;

        showNotification(filterMessage, 'info');
      } else if (prevFilters && (prevFilters.startDate || prevFilters.endDate)) {
        // Cuando se limpian filtros que estaban activos
        showNotification('Filtros eliminados. Mostrando todos los tickets.', 'success');
      }
    }

    return () => {
      isMounted = false;
    };
  }, [dateFilters, showNotification]);

  /** Cierra el modal de crear tablero */
  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  /** Abre el modal para crear una nueva columna */
  const handleCreateColumn = useCallback(() => {
    setCreateColumnModalOpen(true);
  }, []);

  /** Abre el modal para crear nuevos tableros Kanban */
  const handleOpenCreateModal = useCallback(() => {
    // Abrir el modal sin mostrar notificación
    setCreateModalOpen(true);
  }, []);

  /** Maneja la creación de una nueva columna en el tablero actual */
  const handleSaveColumn = useCallback(async (columnData) => {
    try {
      // Filtrar las columnas que pertenecen a este tablero
      const columnasTablero = columns.filter(columna =>
        String(columna.board_id) === String(boardId)
      );

      // Ordenar por posición actual
      const columnasOrdenadas = [...columnasTablero].sort((a, b) => Number(a.position) - Number(b.position));
      
      // Si no hay columnas, usar posición 1 como inicial
      if (columnasOrdenadas.length === 0) {
        columnData.position = 1;
      } else {
        // Insertar en la segunda posición (índice 1)
        columnData.position = 2;
      }
      
      // Añadir flag para identificar creación manual 
      columnData.isManualCreation = true;
      
      // Primero crear la columna, luego reordenar las existentes
      const result = await dispatch(createColumn(boardId, columnData));
      
      // Ahora preparar y reordenar las columnas existentes DESPUÉS de crear la nueva
      if (columnasOrdenadas.length > 0) {
        // Recorrer posiciones de las columnas existentes a partir de la segunda
        const columnasParaActualizar = columnasOrdenadas.filter(col => Number(col.position) >= 2);

        // Preparar columnas para actualizar sus posiciones
        const columnsToUpdate = columnasParaActualizar.map(col => ({
          id: col.id,
          position: Number(col.position) + 1,
          preserveTickets: true
        }));
        
        if (columnsToUpdate.length > 0) {
          try {
            // Obtener el token de autenticación
            const token = columnData.token;

            // Preparar actualizaciones en formato batch
            const batchUpdates = columnsToUpdate.map(col => ({
              id: col.id,
              position: col.position
            }));

            // Actualizar las posiciones usando batch
            await dispatch(updateColumnPositionsBatch(batchUpdates, token));
          } catch (error) {
            console.error('Error al actualizar posiciones de columnas con batch:', error);
          }
        }
      }
      
      showNotification(`Columna "${columnData.name}" creada correctamente`, 'success');
      setCreateColumnModalOpen(false);
      return result;
    } catch (error) {
      console.error('Error al crear columna:', error);
      showNotification(`Error al crear columna: ${error.message}`, 'error');
      throw error;
    }
  }, [boardId, columns, dispatch, showNotification]);

  // Manejadores para el modal de detalles del ticket
  const handleTicketClick = useCallback((ticket) => {
    if (ticket && ticket.id) {
      setTicketIdToOpenDetails(ticket.id);
    }
  }, []);

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedTicket(null);
  };

  const handleTicketCreationSuccess = useCallback((ticket) => {
    // Mostrar notificación de éxito
    showNotification(`Ticket "${ticket.name}" creado exitosamente en ${ticket.column_name}`, 'success');

    // Recargar la columna donde se creó el ticket
    if (kanbanBoardRef.current && ticket.column_id) {
      setTimeout(() => {
        kanbanBoardRef.current.reloadColumnTickets(ticket.column_id);
      }, 500); // Pequeño retraso para asegurar que el servidor ha procesado la creación
    }
  }, [showNotification]);

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default'
    }}>
      {/* Cabecera con botón de crear columna */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {initialBoardData?.name || 'Tablero Kanban General'}
        </Typography>

        {showCreateColumnButton && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<ViewColumnIcon />}
            onClick={handleCreateColumn}
          >
            Añadir Columna
          </Button>
        )}
      </Box>

      {/* Filtro de fechas */}
      <Box sx={{ px: 2 }}>
        <KanbanDateFilter boardId={boardId} />
      </Box>

      {/* Contenido principal del tablero */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'hidden', // Cambiado a hidden para evitar scroll extra
        position: 'relative',
        height: 'auto' // Altura automática basada en el contenido
      }}>
        {formattedColumns.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            gap: 2
          }}>
            <Typography variant="h6" color="textSecondary">
              Este tablero no tiene columnas
            </Typography>
            {showCreateColumnButton && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<ViewColumnIcon />}
                onClick={handleCreateColumn}
              >
                Crear primera columna
              </Button>
            )}
          </Box>
        ) : (
          <KanbanBoard
            ref={kanbanBoardRef}
            boardType="general"
            columns={formattedColumns}
            onTicketClick={handleTicketClick}
            onTicketCreated={handleTicketCreationSuccess}
          />
        )}

        {/* Botón flotante de crear tablero */}
        {showCreateButton && (
          <Tooltip title="Crear tablero" placement="left">
            <span>
              <Fab
                color="primary"
                aria-label="crear-tablero"
                onClick={handleOpenCreateModal}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  zIndex: 999
                }}
              >
                <AddIcon />
              </Fab>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Modales */}
      <CreateBoardModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        boardType="general"
      />

      <CreateColumnModal
        open={createColumnModalOpen}
        onClose={() => setCreateColumnModalOpen(false)}
        onSave={handleSaveColumn}
        boardId={boardId}
      />

      {/* Modal de Detalles del Ticket */}
      {detailModalOpen && selectedTicket && (
        <TicketDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          ticket={selectedTicket}
          boardId={boardId}
        />
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GeneralKanbanView;